import { useMemo } from 'react';
import styled from '@emotion/styled';
import { Divider, message, Space } from 'antd';
import copyText from 'utils/copyText';
import { useTranslate } from '@tolgee/react';
import Icon, { CopyOutlined } from '@ant-design/icons';
import TextButton from 'components/TextButton';
import SellerCenter from 'components/SellerCenter';
import { ReactComponent as LinkSvg } from 'assets/icons/link.svg';
import { useMemoizedFn } from 'ahooks';
import { eventBus } from 'services/eventBus';
import useTrade, { emptyTradeError } from 'views/Review/hooks/useTrade';
import { getOrderSellerCenter } from 'views/Conversation/Sider/utils';
import CardMessage, { CardMessageProps } from './CardMessage';
import { ItemCardData, ItemImg, ItemTitle, Price } from '../../../ItemCard';
import { useChatConversationContext } from 'views/Conversation/Chat/contexts/ChatConversationContext';
import { useTradeOverThreeMonthEnable } from 'hooks/business/useOrgConfig';

interface OrderItem extends ItemCardData {
  amount: number | string;
  link: string;
}

export interface OrderCardMessageData {
  id: string | number;
  invoiceNumber: string | number;
  internalBuyerId: Conversation.Trade.Vo['internalBuyerId'];
  externalBuyerId: Conversation.Trade.Vo['externalBuyerId'];
  createTime: number;
  total: number | string;
  items: OrderItem[];
  tradeStatus?: Conversation.Trade.Vo['tradeStatus'];
  containsPreOrderItem?: Conversation.Trade.Vo['containsPreOrderItem'];
}

export interface OrderCardMessageProps extends CardMessageProps {
  className?: string;
  channel?: string;
  dataSource: OrderCardMessageData;
  isUserEnter?: boolean;
}

const SummaryLine = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: var(--fett-text-secondary);

  ${Price} {
    margin-left: 4px;
  }
`;
const Footer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 4px;
  border-top: 1px dashed var(--fett-border-default);
`;
const InfoContainer = styled.div`
  display: flex;
  align-items: center;
`;
const InfoContent = styled(Space)`
  max-width: calc(100% - 56px);
  margin-left: 8px;

  > .ant-space-item {
    flex: 1;
    display: flex;
  }
`;
const OrderId = styled.span`
  padding: 0 4px;
`;
const ImgWrap = styled.div`
  width: 56px;
  height: 56px;
`;

const OrderCardMessage = (props: OrderCardMessageProps) => {
  const { t } = useTranslate();
  const { currentConversation: conversation } = useChatConversationContext();

  const TRADE_OVER_THREE_MONTH = useTradeOverThreeMonthEnable();

  const {
    className,
    channel,
    dataSource: { id: orderId, invoiceNumber, items, total, externalBuyerId, internalBuyerId },
    isUserEnter = false,
    lazyScrollContainer,
    ...oProps
  } = props;
  const { fetch: fetchTrade } = useTrade(
    conversation!.channel as General.PlatformUpperCase,
    conversation!.storeId,
  );

  const renderItem = useMemo(() => {
    const item = items?.[0];

    const displayOrderId = channel === 'TOKOPEDIA' ? invoiceNumber : orderId;

    return item ? (
      <InfoContainer>
        <ImgWrap>
          <ItemImg hasSmallPic="both" src={item.img} alt="product" target={lazyScrollContainer} />
        </ImgWrap>
        <InfoContent size={0} direction="vertical">
          <ItemTitle>{item.title}</ItemTitle>
          <Space size={0} split={<Divider type="vertical" />} wrap>
            <SummaryLine>
              {t('Entities.订单金额')}
              <Price>{total}</Price>
            </SummaryLine>
            <SummaryLine>
              {t('Entities.订单ID')}
              <OrderId>{displayOrderId}</OrderId>
              <CopyOutlined
                onClick={() => {
                  copyText(displayOrderId)
                    .then(() => {
                      message.success(t('已将订单ID复制到剪贴板'));
                    })
                    .catch((e) => {
                      // eslint-disable-next-line no-console
                      console.log('copy text error', e);
                    });
                }}
              />
            </SummaryLine>
          </Space>
        </InfoContent>
      </InfoContainer>
    ) : null;
  }, [items, orderId, total, channel, invoiceNumber, lazyScrollContainer, t]);

  const handleOrderDetail = useMemoizedFn(async () => {
    try {
      const detail = await fetchTrade({
        internal_buyer_id: internalBuyerId,
        external_buyer_id: externalBuyerId,
        internal_store_id: conversation!.storeId,
        external_trade_ids: orderId.toString(),
        page_no: 1,
        page_size: 1,
      });
      eventBus.emit('CONV_OPEN_SIDE_ORDER_DETAIL', detail);
    } catch (err) {
      if (
        (err as any)?.message === emptyTradeError &&
        channel === 'SHOPEE' &&
        !TRADE_OVER_THREE_MONTH
      ) {
        message.info(
          t('根据Shopee平台政策，超过3个月的订单信息不支持展示，请前往卖家中心查看订单详情'),
        );
      } else {
        message.error(t('api.common.error.messages', { action: t('api.common.action.request') }));
      }
    }
  });

  return (
    <CardMessage className={className} {...oProps}>
      {renderItem}
      <Footer>
        <Space size={0} split={<Divider type="vertical" />}>
          {conversation && conversation.channel === 'LAZADA' ? (
            <SellerCenter platform={conversation.channel}>
              <TextButton
                onClick={() => {
                  getOrderSellerCenter(conversation.storeId, `${orderId}`).catch(() => {
                    message.error(t('sider.打开卖家中心失败，请稍后再试'));
                  });
                }}
              >
                {t('sider.去卖家中心查看')}
                <Icon component={LinkSvg} style={{ marginLeft: 4 }} />
              </TextButton>
            </SellerCenter>
          ) : null}
          <TextButton onClick={handleOrderDetail}>{t('trade.订单详情')}</TextButton>
        </Space>
        {isUserEnter && <div>{t('messageType.SYS_USER_ENTER_FROM_ORDER')}</div>}
      </Footer>
    </CardMessage>
  );
};
export default OrderCardMessage;
